import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ExternalLink, FileText, Plus, Tag, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'nyheter', name: 'Nyheiter' },
  { id: 'budsjett', name: 'Budsjett og økonomi' },
  { id: 'politikk', name: 'Politikk' },
  { id: 'tjenester', name: 'Kommunale tenester' },
  { id: 'utvikling', name: 'Utvikling og planar' },
  { id: 'annet', name: 'Anna' },
];

const ArticleSection = ({ kommuneCode, user }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'link',
    title: '',
    category: 'nyheter',
    url: '',
    description: '',
    content: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canEdit = user && (user.role === 'admin' || user.role === 'editor');

  useEffect(() => {
    fetchArticles();
  }, [kommuneCode]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/kommune/${kommuneCode}/articles`);
      setArticles(response.data);
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  const openEditor = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        type: article.type,
        title: article.title,
        category: article.category,
        url: article.url || '',
        description: article.description || '',
        content: article.content || ''
      });
    } else {
      setEditingArticle(null);
      setFormData({
        type: 'link',
        title: '',
        category: 'nyheter',
        url: '',
        description: '',
        content: ''
      });
    }
    setShowEditor(true);
    setError(null);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingArticle(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        kommune_code: kommuneCode,
        type: formData.type,
        title: formData.title,
        category: formData.category,
        url: formData.type === 'link' ? formData.url : null,
        description: formData.description || null,
        content: formData.type === 'article' ? formData.content : null
      };

      if (editingArticle) {
        await axios.put(`${API}/api/articles/${editingArticle.article_id}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API}/api/kommune/${kommuneCode}/articles`, payload, {
          withCredentials: true
        });
      }

      await fetchArticles();
      closeEditor();
    } catch (err) {
      console.error('Error saving article:', err);
      setError(err.response?.data?.detail || 'Kunne ikkje lagre. Sjekk at du er innlogga med riktig tilgang.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne?')) return;
    
    try {
      await axios.delete(`${API}/api/articles/${articleId}`, {
        withCredentials: true
      });
      await fetchArticles();
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Kunne ikkje slette. Sjekk at du er innlogga med riktig tilgang.');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg" data-testid="articles-section">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#103766]" />
            Artiklar og lenkjer
          </CardTitle>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => openEditor()}
              className="bg-[#103766] hover:bg-[#0d2d52]"
              data-testid="add-article-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Legg til
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {articles.length === 0 ? (
          <p className="text-sm text-[#8AA4BF] text-center py-4">
            Ingen artiklar eller lenkjer enno
          </p>
        ) : (
          <div className="space-y-3">
            {articles.map((article) => (
              <div
                key={article.article_id}
                className="flex items-start justify-between p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors group"
                data-testid={`article-${article.article_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {article.type === 'link' ? (
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#103766] hover:underline font-medium truncate flex items-center gap-1"
                      >
                        {article.title}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    ) : (
                      <Link
                        to={`/artikkel/${article.article_id}`}
                        className="text-[#103766] hover:underline font-medium truncate"
                      >
                        {article.title}
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#8AA4BF]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(article.published_at)}
                    </span>
                    <Badge variant="secondary" className="text-xs bg-[#F0F4F8] text-[#4B647F]">
                      {getCategoryName(article.category)}
                    </Badge>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditor(article)}
                      className="h-8 px-2 text-[#4B647F] hover:text-[#103766]"
                    >
                      Rediger
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(article.article_id)}
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      Slett
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-[600px] bg-white">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Rediger' : 'Legg til artikkel eller lenkje'}
            </DialogTitle>
            <DialogDescription>
              {editingArticle 
                ? 'Oppdater informasjonen under'
                : 'Fyll inn informasjonen for å leggje til ein ny artikkel eller lenkje'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={formData.type === 'link' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, type: 'link' }))}
                className={formData.type === 'link' ? 'bg-[#103766]' : ''}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ekstern lenkje
              </Button>
              <Button
                type="button"
                variant={formData.type === 'article' ? 'default' : 'outline'}
                onClick={() => setFormData(prev => ({ ...prev, type: 'article' }))}
                className={formData.type === 'article' ? 'bg-[#103766]' : ''}
              >
                <FileText className="w-4 h-4 mr-2" />
                Artikkel
              </Button>
            </div>

            {/* Title */}
            <div>
              <label className="text-sm font-medium text-[#1F2A36] mb-1 block">
                Tittel *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Skriv inn tittel"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="text-sm font-medium text-[#1F2A36] mb-1 block">
                Kategori *
              </label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* URL (for links) */}
            {formData.type === 'link' && (
              <div>
                <label className="text-sm font-medium text-[#1F2A36] mb-1 block">
                  URL *
                </label>
                <Input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://..."
                  required
                />
              </div>
            )}

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-[#1F2A36] mb-1 block">
                Kort skildring
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ein kort skildring (valfritt)"
                rows={2}
              />
            </div>

            {/* Content (for articles) */}
            {formData.type === 'article' && (
              <div>
                <label className="text-sm font-medium text-[#1F2A36] mb-1 block">
                  Innhald *
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Skriv artikkelinnhaldet her. Du kan bruke HTML for formatering."
                  rows={8}
                  required
                />
                <p className="text-xs text-[#8AA4BF] mt-1">
                  Tips: Du kan bruke HTML-tagger for formatering (&lt;b&gt;, &lt;i&gt;, &lt;p&gt;, &lt;ul&gt;, etc.)
                </p>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeEditor}>
                Avbryt
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#103766] hover:bg-[#0d2d52]"
              >
                {saving ? 'Lagrer...' : (editingArticle ? 'Oppdater' : 'Legg til')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ArticleSection;
